import { useState, useEffect, useCallback } from 'react';
import { listDoneOrders, listFutureOrders, getAdsDashboard, getAdAppointments, listSamplePreviews } from '../api/adTracker';
import { cldThumb } from '../utils/img';

// ── Shared helpers ──────────────────────────────────────────────────────────

const CHANNEL_STYLE = {
  meta_paid:     'bg-blue-50 text-blue-700 border-blue-200',
  meta_untagged: 'bg-blue-50 text-blue-400 border-blue-100',
  promoter:      'bg-amber-50 text-amber-700 border-amber-200',
  organic:       'bg-green-50 text-green-700 border-green-200',
  repeat:        'bg-purple-50 text-purple-700 border-purple-200',
  manual:        'bg-gray-50 text-gray-600 border-gray-200',
  excluded:      'bg-gray-50 text-gray-400 border-gray-100',
  unknown:       'bg-gray-50 text-gray-400 border-gray-100',
};
const CHANNEL_LABEL = {
  meta_paid: 'Meta', meta_untagged: 'Meta (untagged)', promoter: 'Promoter',
  organic: 'Organic', repeat: 'Repeat', manual: 'Manual', excluded: 'Excluded', unknown: '—',
};

function SourceBadge({ src }) {
  if (!src) return <span className="text-gray-300">—</span>;
  const cls = CHANNEL_STYLE[src.channel] || CHANNEL_STYLE.unknown;
  const tip = src.reattributed
    ? `Reattributed via a follow-up session${src.matched_date ? ' on ' + src.matched_date.slice(0, 10) : ''} — the original click had no ad tag.`
    : undefined;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 w-fit text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${cls}`}>
        {CHANNEL_LABEL[src.channel] || src.channel}
        {src.reattributed && (
          <span title={tip} className="text-blue-500 cursor-help">↩ follow-up</span>
        )}
      </span>
      <span className="text-xs text-gray-600 leading-tight">{src.label}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
function fmtINR(a) { return a == null ? '—' : '₹' + Number(a).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

const STATUS_PILL = {
  completed: 'bg-green-100 text-green-700', rto: 'bg-pink-100 text-pink-700',
  dol: 'bg-orange-100 text-orange-700',
};

// ── Sub-page 1: Done Orders ─────────────────────────────────────────────────

const RANGE_PRESETS = [
  { key: '7d',  label: 'Last 7 days',  days: 7 },
  { key: '14d', label: 'Last 14 days', days: 14 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: 'all', label: 'Lifetime',     days: null },
  { key: 'custom', label: 'Custom',    days: undefined },
];

function useRange(defaultKey = '7d') {
  const [rangeKey, setRangeKey] = useState(defaultKey);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  let date_from, date_to;
  if (rangeKey === 'custom') {
    date_from = customFrom || undefined;
    date_to = customTo || undefined;
  } else {
    const preset = RANGE_PRESETS.find(p => p.key === rangeKey);
    const to = new Date();
    const from = preset?.days ? new Date(Date.now() - (preset.days - 1) * 864e5) : null;
    date_from = from ? ymd(from) : undefined;
    date_to = ymd(to);
  }
  return { rangeKey, setRangeKey, customFrom, setCustomFrom, customTo, setCustomTo, date_from, date_to };
}

function RangeBar({ rangeKey, setRangeKey, customFrom, setCustomFrom, customTo, setCustomTo }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {RANGE_PRESETS.map(p => (
        <button key={p.key} onClick={() => setRangeKey(p.key)}
          className={`px-3 py-1.5 text-sm rounded-full border ${rangeKey === p.key
            ? 'bg-zoddle-green text-white border-zoddle-green'
            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
          {p.label}
        </button>
      ))}
      {rangeKey === 'custom' && (
        <span className="flex items-center gap-1">
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1 text-sm" />
          <span className="text-gray-400">–</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1 text-sm" />
        </span>
      )}
    </div>
  );
}

function DoneOrders() {
  const range = useRange('7d');
  const { date_from, date_to } = range;
  const [data, setData] = useState({ records: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (offset = 0, append = false) => {
    setLoading(true);
    try {
      const r = await listDoneOrders({ date_from, date_to, offset, limit: 25 });
      setData(prev => ({ ...r, records: append ? [...prev.records, ...r.records] : r.records }));
    } finally { setLoading(false); }
  }, [date_from, date_to]);
  useEffect(() => { load(0, false); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <RangeBar {...range} />
        <span className="text-xs text-gray-400">{data.records.length} of {data.total}</span>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            <th className="px-3 py-2">Date</th><th className="px-3 py-2">Order</th>
            <th className="px-3 py-2">Status</th><th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2 text-right">GMV</th><th className="px-3 py-2">Source</th>
          </tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.order_id?.startsWith('rto_dol:') ? r.appointment_uid : r.order_id}</td>
                <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_PILL[r.status] || ''}`}>{r.status}</span></td>
                <td className="px-3 py-2"><div className="font-medium text-gray-800">{r.customer_name || '—'}</div><div className="text-xs text-gray-400">{r.customer_phone || ''}</div></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{r.status === 'completed' ? fmtINR(r.gmv) : '—'}</td>
                <td className="px-3 py-2"><SourceBadge src={r.source} /></td>
              </tr>
            ))}
            {!loading && data.records.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No done orders in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data.records.length < data.total && (
        <button onClick={() => load(data.records.length, true)} disabled={loading}
          className="mt-3 px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-md hover:border-gray-300 disabled:opacity-50">
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
      {!data.leads_fallback_active && (
        <p className="mt-2 text-[11px] text-gray-400">Leads-sheet fallback inactive (LEADS_SHEET_ID not set) — attribution uses bookings only.</p>
      )}
    </div>
  );
}

// ── Sub-page 2: Future Orders ───────────────────────────────────────────────

function FutureOrders() {
  const [data, setData] = useState(null);
  useEffect(() => { listFutureOrders().then(setData).catch(() => setData({ records: [] })); }, []);
  if (!data) return <div className="text-gray-400 py-10 text-center">Loading…</div>;
  return (
    <div>
      <div className="text-xs text-gray-400 mb-3">{data.total} upcoming appointments</div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            <th className="px-3 py-2">Slot</th><th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Status</th><th className="px-3 py-2">This booking</th>
            <th className="px-3 py-2">First touch</th><th className="px-3 py-2">First order</th>
          </tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}<div className="text-xs text-gray-400">{r.time}</div></td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{r.customer_name || '—'}
                    {r.is_repeat && <span className="ml-1 text-[10px] font-bold text-purple-600 bg-purple-50 px-1 rounded">REPEAT ×{r.past_order_count}</span>}
                  </div>
                  <div className="text-xs text-gray-400">{r.customer_phone || ''}</div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.operational_status}</td>
                <td className="px-3 py-2"><SourceBadge src={r.this_source} /></td>
                <td className="px-3 py-2"><SourceBadge src={r.first_touch} /></td>
                <td className="px-3 py-2">
                  {r.first_order_source
                    ? <div><SourceBadge src={r.first_order_source} /><div className="text-[10px] text-gray-400 mt-0.5">{r.first_order_source.order_id} · {fmtDate(r.first_order_source.order_date)}</div></div>
                    : <span className="text-xs text-gray-300">first-time</span>}
                </td>
              </tr>
            ))}
            {data.records.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No upcoming appointments.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Sub-page 3: Live Ads ────────────────────────────────────────────────────

function AdAppointmentsModal({ tag, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { getAdAppointments(tag).then(setData).catch(() => setData({ past: [], upcoming: [] })); }, [tag]);
  const Row = ({ a }) => (
    <tr className="border-b border-gray-50">
      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(a.date)}</td>
      <td className="px-2 py-1.5 text-xs">{a.time}</td>
      <td className="px-2 py-1.5"><div className="text-gray-800">{a.customer_name || '—'}{a.reattributed && <span className="ml-1 text-blue-500 text-[10px]" title="reattributed via follow-up">↩</span>}</div><div className="text-[10px] text-gray-400">{a.customer_phone}</div></td>
      <td className="px-2 py-1.5 text-xs text-gray-500">{a.operational_status}</td>
      <td className="px-2 py-1.5">{a.order_outcome
        ? <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_PILL[a.order_outcome] || ''}`}>{a.order_outcome}</span>
        : <span className="text-xs text-gray-300">—</span>}</td>
    </tr>
  );
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-800">ZOCS appointments · {tag}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        {!data ? <div className="text-gray-400 py-8 text-center">Loading…</div> : (
          <>
            <h4 className="text-xs font-bold text-gray-500 uppercase mb-1">Upcoming ({data.upcoming.length})</h4>
            <table className="w-full text-sm mb-4"><tbody>{data.upcoming.map((a, i) => <Row key={i} a={a} />)}</tbody></table>
            <h4 className="text-xs font-bold text-gray-500 uppercase mb-1">Past ({data.past.length})</h4>
            <table className="w-full text-sm"><tbody>{data.past.map((a, i) => <Row key={i} a={a} />)}</tbody></table>
          </>
        )}
      </div>
    </div>
  );
}

function LiveAds() {
  const range = useRange('7d');
  const { date_from, date_to } = range;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [modalTag, setModalTag] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    getAdsDashboard({ since: date_from, until: date_to })
      .then(setData)
      .catch(e => setErr(e.response?.data?.detail || e.message));
  }, [date_from, date_to]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <RangeBar {...range} />
        {data && <span className="text-[11px] text-gray-400">Meta data as of {new Date(data.pulled_at).toLocaleTimeString()}</span>}
      </div>
      {err && <div className="bg-pink-50 border border-pink-200 text-pink-700 text-sm rounded-md p-3 mb-3">Meta API error: {err}</div>}
      {!data && !err && <div className="text-gray-400 py-10 text-center">Loading Meta insights…</div>}
      {data && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {[['Spend', fmtINR(data.totals.spend)], ['Impressions', data.totals.impressions.toLocaleString()],
              ['Link clicks', data.totals.link_clicks.toLocaleString()], ['Meta bookings', data.totals.meta_bookings],
              ['ZOCS bookings', data.totals.zocs_bookings], ['Realized orders', data.totals.realized]].map(([l, v]) => (
              <div key={l} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-gray-800">{v}</div>
                <div className="text-[10px] text-gray-500 uppercase">{l}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2">Ad</th><th className="px-3 py-2 text-right">Spend</th>
                <th className="px-3 py-2 text-right">CPM</th><th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">CPC</th><th className="px-3 py-2 text-right">Meta bookings</th>
                <th className="px-3 py-2 text-right">₹/booking</th>
                <th className="px-3 py-2 text-right" title="tagged ZOCS bookings > slot ended > phone in orders/RTO">ZOCS y→z→aa</th>
                <th className="px-3 py-2 text-right">Comp %</th><th className="px-3 py-2 text-right">₹/realized</th>
                <th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {data.ads.map((a, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-800">{a.tag || a.ad_name}</div>
                      <div className="text-[11px] text-gray-500">
                        {a.decode ? `${a.decode.ad_set_name || ''}${a.decode.creative_name ? ' · ' + a.decode.creative_name : ' · DCO'} · ${a.decode.link_name || ''}` : a.adset_name}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtINR(a.spend)}</td>
                    <td className="px-3 py-2 text-right">{a.cpm ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{a.link_clicks}</td>
                    <td className="px-3 py-2 text-right">{a.cpc ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{a.bookings}</td>
                    <td className="px-3 py-2 text-right">{a.cost_per_booking ? fmtINR(a.cost_per_booking) : '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {a.funnel.zocs_bookings}{a.funnel.reattributed > 0 &&
                        <span className="text-blue-500" title={`${a.funnel.reattributed} reattributed via follow-up`}> ({a.funnel.reattributed}↩)</span>} → {a.funnel.slot_ended} → {a.funnel.realized}</td>
                    <td className="px-3 py-2 text-right">{a.funnel.completion_rate != null ? `${a.funnel.completion_rate}%` : '—'}</td>
                    <td className="px-3 py-2 text-right">{a.cost_per_realized ? fmtINR(a.cost_per_realized) : '—'}</td>
                    <td className="px-3 py-2">{a.tag && (
                      <button onClick={() => setModalTag(a.tag)} className="text-xs text-zoddle-blue font-semibold hover:underline whitespace-nowrap">Appointments</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 space-y-1">
            {data.caveats.map((c, i) => <p key={i} className="text-[11px] text-gray-400">⚠ {c}</p>)}
          </div>
        </>
      )}
      {modalTag && <AdAppointmentsModal tag={modalTag} onClose={() => setModalTag(null)} />}
    </div>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────

// ── Sample Previews (every skipzy.com/try sample call) ────────────────────────

const REQ_ORDER = ['gender', 'size', 'occasion', 'secondary_occasions', 'ticked_items', 'shoe_style'];

function reqValue(v) {
  if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function SamplePreviews() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (offset = 0) => {
    setLoading(true); setError(null);
    try {
      const d = await listSamplePreviews({ offset, limit: 50 });
      setTotal(d.total);
      setRows(prev => (offset === 0 ? d.rows : [...prev, ...d.rows]));
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(0); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <p className="text-sm text-gray-500">Every sample preview shown on the website: what was asked, and the pieces sent back.</p>
        <button onClick={() => load(0)} className="text-xs text-gray-400 hover:text-zoddle-green">↻ Refresh</button>
        <span className="ml-auto text-xs text-gray-400">{total} previews</span>
      </div>
      {error && <div className="text-sm text-zoddle-pink mb-3">{error}</div>}
      {!loading && rows.length === 0 && !error && <div className="text-center py-16 text-gray-400 text-sm">No previews logged yet.</div>}
      <div className="space-y-3">
        {rows.map(r => {
          const req = r.request || {};
          const keys = [...REQ_ORDER.filter(k => k in req), ...Object.keys(req).filter(k => !REQ_ORDER.includes(k) && k !== 'limit')];
          return (
            <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start gap-6">
                <div className="w-64 shrink-0">
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    {new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <dl className="text-xs space-y-0.5">
                    {keys.map(k => {
                      const v = reqValue(req[k]);
                      return v ? (
                        <div key={k} className="flex gap-1">
                          <dt className="text-gray-400 shrink-0">{k.replace(/_/g, ' ')}:</dt>
                          <dd className="text-gray-700 break-words">{v}</dd>
                        </div>
                      ) : null;
                    })}
                  </dl>
                  <p className="text-[11px] text-gray-400 mt-1">{r.resolved} piece{r.resolved === 1 ? '' : 's'} returned</p>
                </div>
                <div className="flex flex-wrap gap-2 min-w-0">
                  {(r.pieces || []).map((p, i) => (
                    <div key={i} className="w-20 text-[10px] leading-tight">
                      {p.image_url
                        ? <img src={cldThumb(p.image_url, 160)} alt="" className="w-20 h-24 object-cover rounded border border-gray-100" />
                        : <div className="w-20 h-24 bg-gray-50 rounded border border-gray-100" />}
                      <p className="font-semibold text-gray-700 truncate mt-0.5">{p.brand}</p>
                      <p className="font-mono text-gray-500">{p.barcode}</p>
                      <p className="text-gray-700">₹{Math.round(p.price)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {loading && <div className="text-center py-6 text-gray-400 text-sm">Loading…</div>}
      {!loading && rows.length < total && (
        <div className="text-center mt-4">
          <button onClick={() => load(rows.length)} className="text-sm text-zoddle-green font-semibold">Load more</button>
        </div>
      )}
    </div>
  );
}

const SUB_TABS = [
  { key: 'done',   label: 'Done Orders',   Component: DoneOrders },
  { key: 'future', label: 'Future Orders', Component: FutureOrders },
  { key: 'ads',    label: 'Live Ads',      Component: LiveAds },
  { key: 'samples', label: 'Sample Previews', Component: SamplePreviews },
];

export default function AdTracker() {
  const [active, setActive] = useState('done');
  const Active = SUB_TABS.find(t => t.key === active)?.Component;
  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-6 pt-3 flex items-center gap-1">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setActive(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors ${active === t.key
              ? 'text-zoddle-green border-b-2 border-zoddle-green' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-6">{Active && <Active />}</div>
    </div>
  );
}
